import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { PatientTable } from '@/features/patients/components/PatientTable';
// Urgency order for default sort — concern surfaces first
const STATUS_SORT_ORDER = {
    'concern': 0,
    'watch': 1,
    'on-track': 2,
    'awaiting-consent': 3,
    'declined': 4,
    'completed': 5,
    'no-study': 6,
};
const STUB_PATIENTS = [
    {
        id: '1',
        name: 'Ashley Carter',
        enrolledDate: '28 Mar 2026',
        treatmentType: 'Microneedling',
        studyDay: 8,
        studyLength: 21,
        complianceDots: ['both', 'both', 'one', 'both', 'none', 'both', 'both', 'today',
            ...Array(13).fill('future')],
        compliancePct: 79,
        totalScans: 11,
        possibleScans: 14,
        lastScanDate: 'Today',
        status: 'watch',
    },
    {
        id: '2',
        name: 'Jennifer Mitchell',
        enrolledDate: '1 Apr 2026',
        treatmentType: 'Chemical Peel',
        studyDay: null,
        studyLength: 21,
        complianceDots: [],
        compliancePct: null,
        totalScans: null,
        possibleScans: null,
        lastScanDate: null,
        status: 'awaiting-consent',
    },
    {
        id: '3',
        name: 'Sarah Thompson',
        enrolledDate: '15 Mar 2026',
        treatmentType: 'Laser',
        studyDay: 21,
        studyLength: 21,
        complianceDots: ['both', 'both', 'both', 'one', 'both', 'both', 'none', 'both', 'both',
            'both', 'one', 'both', 'both', 'both', 'both', 'one', 'both', 'both', 'none', 'both', 'both'],
        compliancePct: 86,
        totalScans: 36,
        possibleScans: 42,
        lastScanDate: 'Today',
        status: 'completed',
    },
    {
        id: '4',
        name: 'Emily Davis',
        enrolledDate: '2 Apr 2026',
        treatmentType: 'Microneedling',
        studyDay: null,
        studyLength: 21,
        complianceDots: [],
        compliancePct: null,
        totalScans: null,
        possibleScans: null,
        lastScanDate: null,
        status: 'declined',
    },
    {
        id: '5',
        name: 'Michael Johnson',
        enrolledDate: null,
        treatmentType: null,
        studyDay: null,
        studyLength: 21,
        complianceDots: [],
        compliancePct: null,
        totalScans: null,
        possibleScans: null,
        lastScanDate: null,
        status: 'no-study',
    },
];
const FILTER_LABELS = {
    'all': 'All',
    'needs-attention': 'Needs attention',
    'awaiting-consent': 'Awaiting consent',
    'completed': 'Completed',
};
function matchesStatusFilter(patient, filter) {
    if (filter === 'all')
        return true;
    if (filter === 'needs-attention')
        return patient.status === 'concern' || patient.status === 'watch';
    if (filter === 'awaiting-consent')
        return patient.status === 'awaiting-consent';
    if (filter === 'completed')
        return patient.status === 'completed';
    return true;
}
export function PatientsPage() {
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const activePatients = STUB_PATIENTS
        .filter((p) => ['on-track', 'watch', 'concern', 'awaiting-consent', 'declined', 'completed'].includes(p.status))
        .sort((a, b) => STATUS_SORT_ORDER[a.status] - STATUS_SORT_ORDER[b.status]);
    const noStudyPatients = STUB_PATIENTS.filter((p) => p.status === 'no-study');
    const activeCount = activePatients.filter((p) => ['on-track', 'watch', 'concern', 'awaiting-consent'].includes(p.status)).length;
    const avgCompliance = Math.round(activePatients
        .filter((p) => p.compliancePct !== null)
        .reduce((sum, p) => sum + (p.compliancePct ?? 0), 0) /
        (activePatients.filter((p) => p.compliancePct !== null).length || 1));
    const needsAttention = STUB_PATIENTS.filter((p) => p.status === 'watch' || p.status === 'concern').length;
    const completedCount = STUB_PATIENTS.filter((p) => p.status === 'completed').length;
    function applyFilters(rows) {
        return rows.filter((p) => {
            const matchesSearch = !search.trim() || p.name.toLowerCase().includes(search.toLowerCase());
            return matchesSearch && matchesStatusFilter(p, statusFilter);
        });
    }
    const filteredActive = applyFilters(activePatients);
    const filteredNoStudy = statusFilter === 'all' ? applyFilters(noStudyPatients) : [];
    const hasResults = filteredActive.length > 0 || filteredNoStudy.length > 0;
    return (_jsxs("div", { className: "flex flex-col gap-5", children: [_jsx("div", { className: "flex items-center justify-between", children: _jsx("h1", { className: "text-[22px] font-bold text-on-surface", children: "Patients" }) }), _jsxs("div", { className: "flex gap-3", children: [_jsxs("div", { className: "flex-1 bg-surface-container-low rounded-[10px] px-4 py-3 flex flex-col gap-0.5 border border-outline-variant", children: [_jsx("span", { className: "text-[22px] font-bold leading-tight text-on-surface", children: activeCount }), _jsxs("span", { className: "text-[12px] font-normal text-on-surface-variant", children: ["Active stud", activeCount === 1 ? 'y' : 'ies', " across ", activeCount, " patient", activeCount === 1 ? '' : 's'] })] }), _jsxs("div", { className: "flex-1 bg-surface-container-low rounded-[10px] px-4 py-3 flex flex-col gap-0.5 border border-outline-variant", children: [_jsxs("span", { className: "text-[22px] font-bold leading-tight text-on-surface", children: [avgCompliance, "%"] }), _jsx("span", { className: "text-[12px] font-normal text-on-surface-variant", children: "Avg compliance last 7 days" })] }), _jsxs("div", { className: `flex-1 rounded-[10px] px-4 py-3 flex flex-col gap-0.5 border transition-colors ${needsAttention > 0
                            ? 'bg-[#dc26261a] border-status-concern border-l-[3px]'
                            : 'bg-surface-container-low border-outline-variant'}`, children: [_jsx("span", { className: `text-[22px] font-bold leading-tight ${needsAttention > 0 ? 'text-status-concern' : 'text-on-surface'}`, children: needsAttention }), _jsx("span", { className: "text-[12px] font-normal text-on-surface-variant", children: "Need attention \u2014 concern or watch" })] }), _jsxs("div", { className: "flex-1 bg-surface-container-low rounded-[10px] px-4 py-3 flex flex-col gap-0.5 border border-outline-variant", children: [_jsx("span", { className: "text-[22px] font-bold leading-tight text-on-surface", children: completedCount }), _jsx("span", { className: "text-[12px] font-normal text-on-surface-variant", children: "Completed this month" })] })] }), _jsxs("div", { className: "flex items-center gap-3 flex-wrap", children: [_jsxs("div", { className: "relative flex-1 max-w-xs", children: [_jsxs("svg", { className: "absolute left-3 top-1/2 -translate-y-1/2 text-on-surface-variant", width: "15", height: "15", viewBox: "0 0 15 15", fill: "none", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round", children: [_jsx("circle", { cx: "6.5", cy: "6.5", r: "4.5" }), _jsx("path", { d: "M10.5 10.5l2.5 2.5" })] }), _jsx("input", { type: "text", placeholder: "Search by name\u2026", value: search, onChange: (e) => setSearch(e.target.value), className: "w-full h-9 pl-9 pr-3 bg-surface-container-low border border-outline-variant rounded-[8px] text-[13px] text-on-surface placeholder:text-on-surface-variant focus:outline-none focus:border-primary focus:border-2" })] }), _jsx("div", { className: "flex items-center gap-1.5", children: Object.keys(FILTER_LABELS).map((f) => (_jsx("button", { onClick: () => setStatusFilter(f), className: `h-8 px-3 rounded-full text-[12px] font-medium transition-colors ${statusFilter === f
                                ? 'bg-primary text-on-primary'
                                : 'bg-surface-container-low text-on-surface-variant border border-outline-variant hover:bg-surface-container hover:text-on-surface'}`, children: FILTER_LABELS[f] }, f))) })] }), filteredActive.length > 0 && (_jsx(PatientTable, { label: "ACTIVE", count: filteredActive.length, patients: filteredActive })), filteredNoStudy.length > 0 && (_jsx(PatientTable, { label: "NO STUDY", count: filteredNoStudy.length, patients: filteredNoStudy })), !hasResults && (_jsx("p", { className: "text-[14px] text-on-surface-variant text-center py-12", children: search.trim() ? `No patients match "${search}"` : 'No patients in this filter.' }))] }));
}
